import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@shared/services/api";
import { ADMIN_CATALOG_KEYS } from "@shared/constants/queryKeys";
import type {
  CreateCatalogData,
  UpdateCatalogData,
  Catalog,
} from "@admin/types/catalog";

type CatalogApiResponse = {
  success?: boolean;
  data?: Catalog;
  message?: string;
} & Partial<Catalog>;

const extractCatalog = (response: CatalogApiResponse | Catalog): Catalog => {
  if ("data" in response && response.data) {
    return response.data;
  }
  return response as Catalog;
};

export function useCreateCatalog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCatalogData): Promise<Catalog> => {
      const response = await apiClient.post<CatalogApiResponse>("/catalog", data);
      return extractCatalog(response);
    },
    onSuccess: () => {
      // Admin
      queryClient.invalidateQueries({ queryKey: ADMIN_CATALOG_KEYS.all });

      // Público: catálogos
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "catalogs",
      });

      // Home / navbar
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
      queryClient.invalidateQueries({ queryKey: ["navbar"] });
    },
  });
}

export function useUpdateCatalog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateCatalogData): Promise<Catalog> => {
      const { id, ...updateData } = data;
      const response = await apiClient.put<CatalogApiResponse>(
        `/catalog/${id}`,
        updateData
      );
      return extractCatalog(response);
    },
    onSuccess: () => {
      // Admin
      queryClient.invalidateQueries({ queryKey: ADMIN_CATALOG_KEYS.all });

      // Público: catálogos
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "catalogs",
      });

      // Home / navbar
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
      queryClient.invalidateQueries({ queryKey: ["navbar"] });
    },
  });
}

export function useDeleteCatalog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/catalog/${id}`);
    },
    onSuccess: () => {
      // Admin
      queryClient.invalidateQueries({ queryKey: ADMIN_CATALOG_KEYS.all });

      // Público: catálogos
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "catalogs",
      });

      // Home / navbar
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
      queryClient.invalidateQueries({ queryKey: ["navbar"] });
    },
  });
}

export function useToggleCatalogStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      currentActive,
    }: {
      id: string;
      currentActive: boolean;
    }): Promise<Catalog> => {
      const endpoint = currentActive
        ? `/catalog/${id}/deactivate`
        : `/catalog/${id}/activate`;
      const response = await apiClient.patch<CatalogApiResponse>(endpoint);
      return extractCatalog(response);
    },
    onSuccess: () => {
      // Admin
      queryClient.invalidateQueries({ queryKey: ADMIN_CATALOG_KEYS.all });

      // Público: catálogos
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "catalogs",
      });

      // Home / navbar
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
      queryClient.invalidateQueries({ queryKey: ["navbar"] });
    },
  });
}

export function useUpdateCatalogOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      order,
    }: {
      id: string;
      order: number;
    }): Promise<Catalog> => {
      const response = await apiClient.patch<CatalogApiResponse>(
        `/catalog/${id}/order`,
        { order }
      );
      return extractCatalog(response);
    },
    onSuccess: () => {
      // Admin
      queryClient.invalidateQueries({ queryKey: ADMIN_CATALOG_KEYS.all });

      // Público: catálogos
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "catalogs",
      });

      // Home / navbar
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
      queryClient.invalidateQueries({ queryKey: ["navbar"] });
    },
  });
}

